class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      // Don't close if we're currently adding to cart
      if (this.isAddingToCart) return;
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // Request both products and articles (recipes)
    // Use comma-separated format: resources[type]=product,article
    const queryString = [
      `q=${encodeURIComponent(searchTerm)}`,
      `resources[type]=product,article`,
      `resources[limit]=4`,
      `resources[limit_scope]=each`,
      `section_id=predictive-search`
    ].join('&');
    
    fetch(`${routes.predictive_search_url}?${queryString}`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const section = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
        if (!section) {
          return;
        }
        const resultsMarkup = section.innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
    
    // Setup add to cart handlers for the newly loaded results
    this.setupAddToCartHandlers();
    
    // Setup tab switching if both products and recipes are available
    this.setupTabSwitching();
    
    // Ensure products are shown by default if tabs exist
    const tabs = this.predictiveSearchResults.querySelectorAll('.predictive-search__tab');
    if (tabs.length > 0) {
      const productsTab = this.predictiveSearchResults.querySelector('[data-tab="products"]');
      const recipesTab = this.predictiveSearchResults.querySelector('[data-tab="recipes"]');
      const productsContent = this.predictiveSearchResults.querySelector('#predictive-search-tab-content-products');
      const recipesContent = this.predictiveSearchResults.querySelector('#predictive-search-tab-content-recipes');
      
      // Ensure products tab is active and products content is visible by default
      if (productsTab && productsContent && recipesContent) {
        productsContent.style.display = 'block';
        recipesContent.style.display = 'none';
        productsTab.classList.add('predictive-search__tab--active');
        recipesTab.classList.remove('predictive-search__tab--active');
        productsTab.setAttribute('aria-selected', 'true');
        recipesTab.setAttribute('aria-selected', 'false');
        productsTab.style.fontWeight = '600';
        recipesTab.style.fontWeight = '400';
        productsTab.style.color = 'rgb(var(--color-foreground))';
        recipesTab.style.color = 'rgba(var(--color-foreground), 0.6)';
        productsTab.style.borderBottomColor = 'rgb(var(--color-foreground))';
        recipesTab.style.borderBottomColor = 'transparent';
      }
    }
  }
  
  setupTabSwitching() {
    const tabs = this.predictiveSearchResults.querySelectorAll('.predictive-search__tab');
    const tabContents = this.predictiveSearchResults.querySelectorAll('.predictive-search__tab-content');
    
    if (tabs.length === 0) return; // No tabs, so no switching needed
    
    tabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        e.preventDefault();
        const targetTab = tab.dataset.tab;
        
        // Update tab states
        tabs.forEach(t => {
          const isActive = t === tab;
          t.classList.toggle('predictive-search__tab--active', isActive);
          t.setAttribute('aria-selected', isActive);
          t.style.fontWeight = isActive ? '600' : '400';
          t.style.color = isActive ? 'rgb(var(--color-foreground))' : 'rgba(var(--color-foreground), 0.6)';
          t.style.borderBottomColor = isActive ? 'rgb(var(--color-foreground))' : 'transparent';
        });
        
        // Show/hide tab content
        tabContents.forEach(content => {
          const isProducts = content.id === 'predictive-search-tab-content-products';
          const isRecipes = content.id === 'predictive-search-tab-content-recipes';
          const shouldShow = (targetTab === 'products' && isProducts) || (targetTab === 'recipes' && isRecipes);
          
          content.style.display = shouldShow ? 'block' : 'none';
          content.setAttribute('aria-hidden', !shouldShow);
        });
        
        // Reset selected option in listbox
        const activeList = this.predictiveSearchResults.querySelector(`#predictive-search-results-list-${targetTab}`);
        if (activeList) {
          const selected = activeList.querySelector('[aria-selected="true"]');
          if (selected) {
            selected.setAttribute('aria-selected', 'false');
          }
          this.input.setAttribute('aria-activedescendant', '');
        }
      });
    });
  }
  
  setupAddToCartHandlers() {
    const addToCartButtons = this.predictiveSearchResults.querySelectorAll('.predictive-search-add-to-cart');
    
    addToCartButtons.forEach(button => {
      // Remove any existing listeners to prevent duplicates
      button.removeEventListener('click', this.handleAddToCart);
      // Add the listener
      button.addEventListener('click', this.handleAddToCart.bind(this));
    });
  }
  
  async handleAddToCart(event) {
    event.preventDefault();
    event.stopPropagation();
    
    const button = event.currentTarget;
    const variantId = button.dataset.variantId;
    
    if (!variantId) {
      return;
    }
    
    const buttonText = button.querySelector('.button-text');
    const originalText = buttonText ? buttonText.textContent : 'Add to Cart';
    
    // Prevent predictive search from closing while adding to cart
    this.isAddingToCart = true;
    
    // Show loading state
    button.disabled = true;
    if (buttonText) buttonText.textContent = 'Adding...';
    
    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.description || 'Failed to add to cart');
      }
      
      await response.json();
      
      // Refresh cart and open drawer
      window.dispatchEvent(new CustomEvent('cart:refresh'));
      window.dispatchEvent(new CustomEvent('cart:open'));
      
      // Show success
      if (buttonText) buttonText.textContent = 'Added!';
      
      // Wait for cart drawer to open, then allow predictive search to close
      setTimeout(() => {
        if (buttonText) buttonText.textContent = originalText;
        button.disabled = false;
        this.isAddingToCart = false;
        
        // Close predictive search now that cart is open
        this.close();
      }, 800);
      
    } catch (error) {
      if (buttonText) buttonText.textContent = 'Error';
      
      setTimeout(() => {
        if (buttonText) buttonText.textContent = originalText;
        button.disabled = false;
        this.isAddingToCart = false;
      }, 2000);
    }
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    const modalWrapper = document.querySelector('.main-header-group--wrapper.search--modal-main-wrapper');
    if (modalWrapper) {
      this.resultsMaxHeight = window.innerHeight - modalWrapper.getBoundingClientRect().bottom;
    } else {
      // Fallback for header search (e.g., mega-menu)
      const searchForm = this.querySelector('form.search');
      if (searchForm) {
        this.resultsMaxHeight = window.innerHeight - searchForm.getBoundingClientRect().bottom - 20;
      } else {
        this.resultsMaxHeight = 400; // Default fallback
      }
    }
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');

    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);